import { memo } from 'react';

/**
 * Memoised world-specific asset SVG — one decorative variant per environment.
 * Props: size, variant (environment name)
 */
export const WorldAssetSvg = memo(function WorldAssetSvg({ size, variant = 'forest' }) {
  let inner;
  if (variant === 'forest') {
    // Flowers — a cluster of three coloured blooms
    inner = <>
      {/* Stems */}
      <line x1="30" y1="44" x2="30" y2="56" stroke="#4a8820" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="16" y1="50" x2="16" y2="57" stroke="#4a8820" strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="44" y1="48" x2="44" y2="57" stroke="#4a8820" strokeWidth="1.2" strokeLinecap="round"/>
      {/* Leaves */}
      <ellipse cx="26" cy="51" rx="5" ry="2" fill="#3a8820" transform="rotate(-30 26 51)"/>
      <ellipse cx="34" cy="53" rx="5" ry="2" fill="#3a8820" transform="rotate(30 34 53)"/>
      {/* Centre flower */}
      <ellipse cx="30" cy="36" rx="3" ry="6" fill="#ff6688"/>
      <ellipse cx="30" cy="44" rx="3" ry="6" fill="#ff6688"/>
      <ellipse cx="22" cy="40" rx="6" ry="3" fill="#ff6688"/>
      <ellipse cx="38" cy="40" rx="6" ry="3" fill="#ff6688"/>
      <ellipse cx="24" cy="34" rx="3.5" ry="5" fill="#ff88aa" transform="rotate(-45 24 34)"/>
      <ellipse cx="36" cy="34" rx="3.5" ry="5" fill="#ff88aa" transform="rotate(45 36 34)"/>
      <ellipse cx="24" cy="46" rx="3.5" ry="5" fill="#ff88aa" transform="rotate(45 24 46)"/>
      <ellipse cx="36" cy="46" rx="3.5" ry="5" fill="#ff88aa" transform="rotate(-45 36 46)"/>
      <circle cx="30" cy="40" r="3.5" fill="#ffdd00"/>
      {/* Left flower */}
      <ellipse cx="16" cy="46" rx="2.5" ry="4" fill="#ff8844"/>
      <ellipse cx="16" cy="52" rx="2.5" ry="4" fill="#ff8844"/>
      <ellipse cx="12" cy="49" rx="4" ry="2.5" fill="#ff8844"/>
      <ellipse cx="20" cy="49" rx="4" ry="2.5" fill="#ff8844"/>
      <circle cx="16" cy="49" r="2.5" fill="#ffdd00"/>
      {/* Right flower */}
      <ellipse cx="44" cy="44" rx="2.5" ry="4" fill="#aa44ff"/>
      <ellipse cx="44" cy="50" rx="2.5" ry="4" fill="#aa44ff"/>
      <ellipse cx="40" cy="47" rx="4" ry="2.5" fill="#aa44ff"/>
      <ellipse cx="48" cy="47" rx="4" ry="2.5" fill="#aa44ff"/>
      <circle cx="44" cy="47" r="2.5" fill="#ffdd00"/>
    </>;
  } else if (variant === 'desert') {
    // Camel — simplified silhouette
    inner = <>
      <ellipse cx="30" cy="57" rx="18" ry="3" fill="rgba(0,0,0,0.20)"/>
      {/* Body */}
      <ellipse cx="30" cy="40" rx="16" ry="10" fill="#c8a040" stroke="#8a6820" strokeWidth="1"/>
      {/* Hump */}
      <ellipse cx="34" cy="30" rx="9" ry="8" fill="#c8a040" stroke="#8a6820" strokeWidth="1"/>
      {/* Neck */}
      <rect x="13" y="28" width="9" height="16" rx="4.5" fill="#c8a040" stroke="#8a6820" strokeWidth="1"/>
      {/* Head */}
      <ellipse cx="13" cy="24" rx="7" ry="6" fill="#c8a040" stroke="#8a6820" strokeWidth="1"/>
      {/* Ear */}
      <ellipse cx="10" cy="19" rx="2.5" ry="3.5" fill="#c8a040" stroke="#8a6820" strokeWidth="0.8"/>
      {/* Eye */}
      <circle cx="10" cy="23" r="1.5" fill="#3a2a10"/>
      {/* Snout */}
      <ellipse cx="7"  cy="26" rx="3.5" ry="2.5" fill="#d4aa50" stroke="#8a6820" strokeWidth="0.8"/>
      {/* Legs */}
      <rect x="16" y="48" width="5" height="11" rx="2.5" fill="#b88030" stroke="#7a5820" strokeWidth="0.8"/>
      <rect x="24" y="48" width="5" height="11" rx="2.5" fill="#b88030" stroke="#7a5820" strokeWidth="0.8"/>
      <rect x="33" y="48" width="5" height="11" rx="2.5" fill="#b88030" stroke="#7a5820" strokeWidth="0.8"/>
      <rect x="41" y="48" width="5" height="11" rx="2.5" fill="#b88030" stroke="#7a5820" strokeWidth="0.8"/>
      {/* Tail */}
      <path d="M46 42 Q53 40 50 47" stroke="#b88030" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </>;
  } else if (variant === 'water') {
    // Coral — branching polyp structure
    inner = <>
      <ellipse cx="30" cy="57" rx="14" ry="3" fill="rgba(0,0,0,0.20)"/>
      {/* Main stem */}
      <rect x="27" y="18" width="6" height="38" rx="3" fill="#e86050" stroke="#c04030" strokeWidth="1"/>
      {/* Left branch */}
      <rect x="17" y="28" width="5" height="22" rx="2.5" fill="#e87060" stroke="#c04030" strokeWidth="1"/>
      {/* Right branch */}
      <rect x="38" y="24" width="5" height="24" rx="2.5" fill="#e86050" stroke="#c04030" strokeWidth="1"/>
      {/* Far left */}
      <rect x="9"  y="36" width="4" height="16" rx="2" fill="#e88070" stroke="#c04030" strokeWidth="0.8"/>
      {/* Far right */}
      <rect x="47" y="32" width="4" height="18" rx="2" fill="#e88070" stroke="#c04030" strokeWidth="0.8"/>
      {/* Polyp tips */}
      <circle cx="30" cy="15" r="5"   fill="#ff9090" stroke="#c04030" strokeWidth="1"/>
      <circle cx="19" cy="25" r="4"   fill="#ff9090" stroke="#c04030" strokeWidth="1"/>
      <circle cx="40" cy="21" r="4"   fill="#ff9090" stroke="#c04030" strokeWidth="1"/>
      <circle cx="11" cy="33" r="3"   fill="#ff9090" stroke="#c04030" strokeWidth="0.8"/>
      <circle cx="49" cy="29" r="3"   fill="#ff9090" stroke="#c04030" strokeWidth="0.8"/>
      {/* Highlight dots */}
      <circle cx="28" cy="20" r="2" fill="#ffb0a0"/>
      <circle cx="33" cy="23" r="2" fill="#ffb0a0"/>
    </>;
  } else if (variant === 'castle') {
    // Weapon rack — horizontal bar with crossed swords + axe
    inner = <>
      {/* Vertical posts */}
      <rect x="7"  y="20" width="5" height="38" rx="2.5" fill="#6a5030" stroke="#3a2a18" strokeWidth="1"/>
      <rect x="48" y="20" width="5" height="38" rx="2.5" fill="#6a5030" stroke="#3a2a18" strokeWidth="1"/>
      {/* Horizontal bar */}
      <rect x="6"  y="26" width="48" height="6" rx="3" fill="#6a5030" stroke="#3a2a18" strokeWidth="1"/>
      {/* Left sword */}
      <line x1="17" y1="13" x2="22" y2="42" stroke="#909090" strokeWidth="3.5" strokeLinecap="round"/>
      <rect x="13" y="13" width="9" height="3.5" rx="1.5" fill="#c8a030" stroke="#886810" strokeWidth="0.8"/>
      <ellipse cx="17" cy="11" rx="3.5" ry="2.5" fill="#c8a030" stroke="#886810" strokeWidth="0.8"/>
      {/* Right sword */}
      <line x1="43" y1="13" x2="38" y2="42" stroke="#909090" strokeWidth="3.5" strokeLinecap="round"/>
      <rect x="38" y="13" width="9" height="3.5" rx="1.5" fill="#c8a030" stroke="#886810" strokeWidth="0.8"/>
      <ellipse cx="43" cy="11" rx="3.5" ry="2.5" fill="#c8a030" stroke="#886810" strokeWidth="0.8"/>
      {/* Centre axe */}
      <line x1="30" y1="8" x2="30" y2="44" stroke="#886830" strokeWidth="3" strokeLinecap="round"/>
      <path d="M30 14 Q20 10 22 22 Q26 18 30 20Z" fill="#808080" stroke="#505050" strokeWidth="0.8"/>
      <path d="M30 14 Q40 10 38 22 Q34 18 30 20Z" fill="#808080" stroke="#505050" strokeWidth="0.8"/>
      <ellipse cx="30" cy="46" rx="4" ry="3" fill="#886830" stroke="#5a4020" strokeWidth="0.8"/>
    </>;
  } else if (variant === 'graveyard') {
    // Tombstone — weathered stone slab with cross
    inner = <>
      <ellipse cx="30" cy="57" rx="16" ry="3" fill="rgba(0,0,0,0.28)"/>
      {/* Stone body */}
      <rect x="10" y="22" width="40" height="34" rx="2" fill="#484458" stroke="#2e2a38" strokeWidth="1.2"/>
      {/* Rounded top */}
      <ellipse cx="30" cy="22" rx="20" ry="11" fill="#484458" stroke="#2e2a38" strokeWidth="1.2"/>
      {/* Cross */}
      <rect x="27" y="26" width="6" height="20" rx="1.5" fill="#302c3a"/>
      <rect x="20" y="32" width="20" height="6"  rx="1.5" fill="#302c3a"/>
      {/* RIP text */}
      <text x="30" y="52" textAnchor="middle" fontSize="7" fill="#6a6480" fontFamily="serif" opacity="0.9">R.I.P</text>
      {/* Weathering crack */}
      <line x1="15" y1="30" x2="20" y2="44" stroke="#2e2a38" strokeWidth="1" opacity="0.55"/>
      {/* Mushroom at base */}
      <ellipse cx="44" cy="53" rx="5.5" ry="3" fill="#8060a8" opacity="0.85"/>
      <rect x="42" y="53" width="4" height="5" rx="1" fill="#5a4070" opacity="0.85"/>
    </>;
  } else if (variant === 'snow') {
    // Snowman — three stacked balls with hat, scarf, arms
    inner = <>
      <ellipse cx="30" cy="57" rx="15" ry="3" fill="rgba(0,0,0,0.18)"/>
      {/* Bottom ball */}
      <circle cx="30" cy="44" r="13" fill="white" stroke="#b0c8e0" strokeWidth="1.2"/>
      {/* Mid ball */}
      <circle cx="30" cy="25" r="9"  fill="white" stroke="#b0c8e0" strokeWidth="1.2"/>
      {/* Head */}
      <circle cx="30" cy="11" r="6"  fill="white" stroke="#b0c8e0" strokeWidth="1.2"/>
      {/* Eyes */}
      <circle cx="27" cy="9"  r="1.4" fill="#2a3a4a"/>
      <circle cx="33" cy="9"  r="1.4" fill="#2a3a4a"/>
      {/* Carrot nose */}
      <polygon points="30,12 28,14 35,13" fill="#e86020"/>
      {/* Smile dots */}
      <circle cx="27" cy="15" r="1"   fill="#2a3a4a"/>
      <circle cx="30" cy="16" r="1"   fill="#2a3a4a"/>
      <circle cx="33" cy="15" r="1"   fill="#2a3a4a"/>
      {/* Hat */}
      <rect x="23" y="2" width="14" height="8" rx="1.5" fill="#2a3a4a"/>
      <rect x="20" y="9" width="20" height="3" rx="1.5" fill="#2a3a4a"/>
      {/* Scarf */}
      <path d="M21 17 Q30 21 39 17" stroke="#e83030" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
      <line x1="24" y1="19" x2="22" y2="24" stroke="#e83030" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Buttons */}
      <circle cx="30" cy="23" r="1.5" fill="#4a5a6a"/>
      <circle cx="30" cy="29" r="1.5" fill="#4a5a6a"/>
      {/* Stick arms */}
      <line x1="21" y1="22" x2="10" y2="16" stroke="#6a4a2a" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="39" y1="22" x2="50" y2="16" stroke="#6a4a2a" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="10" y1="16" x2="7"  y2="12" stroke="#6a4a2a" strokeWidth="2" strokeLinecap="round"/>
      <line x1="50" y1="16" x2="53" y2="12" stroke="#6a4a2a" strokeWidth="2" strokeLinecap="round"/>
    </>;
  } else {
    // inferno — fire column
    inner = <>
      <ellipse cx="30" cy="56" rx="16" ry="4" fill="#ff4400" opacity="0.35"/>
      {/* Outer flame */}
      <path d="M12,56 Q10,42 17,30 Q14,20 22,10 Q24,22 27,18 Q30,22 33,18 Q38,20 35,30 Q42,42 40,56Z" fill="#e83000" opacity="0.72"/>
      {/* Mid flame */}
      <path d="M16,56 Q14,44 20,34 Q17,26 24,18 Q25,28 27,24 Q30,26 33,24 Q37,26 34,34 Q40,44 38,56Z" fill="#f86000" opacity="0.80"/>
      {/* Inner flame */}
      <path d="M19,56 Q18,46 22,38 Q20,32 25,26 Q27,32 27,36 Q30,30 33,26 Q36,32 32,38 Q35,46 33,56Z" fill="#ffaa00" opacity="0.86"/>
      {/* Core */}
      <path d="M23,56 Q22,48 25,42 Q23,37 28,32 Q31,37 29,42 Q32,48 31,56Z" fill="#ffee60" opacity="0.92"/>
      {/* Sparks */}
      <circle cx="20" cy="28" r="2"   fill="#ffcc00" opacity="0.82"/>
      <circle cx="40" cy="24" r="2"   fill="#ffcc00" opacity="0.82"/>
      <circle cx="15" cy="40" r="1.5" fill="#ff8800" opacity="0.72"/>
      <circle cx="44" cy="36" r="1.5" fill="#ff8800" opacity="0.72"/>
      <circle cx="26" cy="16" r="1.5" fill="#fff080" opacity="0.85"/>
    </>;
  }
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" fill="none"
      xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}
    >
      {inner}
    </svg>
  );
});
